import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import PlayerListAttachPicker from '../components/PlayerListAttachPicker';
import { DateRangePills } from '../components/DateRangePills';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { getSellbotExportStatsUrl } from '../api/generated/sellbot';
import { useActiveBot } from '../state/activeBot';
import { useDebounced } from '../hooks/useDebounced';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useItemName } from '../hooks/useItemName';
import { useTablePref } from '../hooks/useTablePrefs';
import { useTranslation } from '../hooks/useTranslation';
import { downloadFromApi } from '../services/downloadFile';
import { formatQuantity } from '../services/quantityFormatter';
import type { PlayerListDto, SellbotDayBucketDto, SellbotGetStatsParams, SellbotStatsDto } from '../api';
import styles from './Sellbot.module.css';

type RangePreset = 'today' | '7d' | '30d' | '90d' | 'lastMonth' | 'all' | 'custom';
type DayMetric = 'umsatz' | 'selling' | 'buying';

interface StatsFilters {
    preset: RangePreset;
    customFrom: string;
    customTo: string;
    player: string | null;
    playerListIds: string[];
}

const DEFAULT_FILTERS: StatsFilters = { preset: '30d', customFrom: '', customTo: '', player: null, playerListIds: [] };

const DAY_METRICS: { value: DayMetric; labelKey: string }[] = [
    { value: 'umsatz', labelKey: 'SELLBOT_STATS_DAY_METRIC_UMSATZ' },
    { value: 'selling', labelKey: 'SELLBOT_STATS_DAY_METRIC_SELLING' },
    { value: 'buying', labelKey: 'SELLBOT_STATS_DAY_METRIC_BUYING' },
];

const DAY_REF = new Date(2023, 0, 1); // a Sunday; DayOfWeek 0 = Sunday, matching Date#getDay()
const dayLabel = (day: number) => new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(new Date(DAY_REF.getTime() + day * 86400000));

// Backend DayOfWeek is Sunday=0..Saturday=6; reorder to the European Monday-first week for display.
const MONDAY_FIRST: readonly number[] = [1, 2, 3, 4, 5, 6, 0];
const mondayFirst = (days: SellbotDayBucketDto[]): SellbotDayBucketDto[] =>
    [...days].sort((a, b) => MONDAY_FIRST.indexOf(a.day) - MONDAY_FIRST.indexOf(b.day));

function dayMetricSeries(metric: DayMetric, s: SellbotStatsDto, fmtMoney: (n: number) => string, fmtNum: (n: number) => string) {
    switch (metric) {
        case 'selling': return { days: s.salesByDayOfWeek, value: (d: SellbotDayBucketDto) => d.count, format: fmtNum };
        case 'buying': return { days: s.ankaufByDayOfWeek, value: (d: SellbotDayBucketDto) => d.amount, format: fmtMoney };
        default: return { days: s.salesByDayOfWeek, value: (d: SellbotDayBucketDto) => d.amount, format: fmtMoney };
    }
}

export default function SellbotStats() {
    const api = useApi();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const { itemName, stackSize } = useItemName();
    const statsItemName = (key?: string | null, snbt?: string | null) => key ? itemName(key, snbt) : t('SELLBOT_STATS_UNKNOWN_ITEM');
    const statsStackSize = (key?: string | null) => key ? stackSize(key) : undefined;
    const fmtNum = (n: number) => new Intl.NumberFormat('de-DE').format(n);
    const fmtMoney = (n: number) => `${fmtNum(n)}$`;

    const toast = useToast();
    const playerListEnabled = useFeatureFlag('PLAYER_LIST');

    const [storedFilters, setStoredFilters] = useTablePref<Partial<StatsFilters>>('sellbotStats.filters', DEFAULT_FILTERS);
    const filters = createMemo<StatsFilters>(() => ({ ...DEFAULT_FILTERS, ...storedFilters() }));
    const patchFilters = (patch: Partial<StatsFilters>) => setStoredFilters({ ...filters(), ...patch });
    const [dayMetric, setDayMetric] = createSignal<DayMetric>('umsatz');
    const [exporting, setExporting] = createSignal(false);

    const [customerQuery, setCustomerQuery] = createSignal('');

    const [playerLists] = createResource(
        () => playerListEnabled(),
        async () => {
            const res = await api.playerList.listPlayerLists();
            return isOk(res.data) ? res.data.value.lists ?? [] : [] as PlayerListDto[];
        }
    );

    // A stored list id the user has since lost access to would turn the whole request into a 403.
    const activeListIds = createMemo(() => {
        if (!playerListEnabled()) return [] as string[];
        const lists = playerLists();
        const ids = filters().playerListIds;
        return lists ? ids.filter((id) => lists.some((list) => list.id === id)) : ids;
    });
    const selectedLists = createMemo(() => (playerLists() ?? [])
        .filter((list) => activeListIds().includes(list.id))
        .map((list) => ({ id: list.id, name: list.name, memberCount: list.memberCount })));

    const attachList = (id: string) => patchFilters({ playerListIds: [...filters().playerListIds, id] });
    const detachList = (id: string) => patchFilters({ playerListIds: filters().playerListIds.filter((listId) => listId !== id) });

    const debouncedCustomerQuery = useDebounced(customerQuery, 200);
    const [customerSuggestions] = createResource(
        () => {
            const id = botId();
            const query = debouncedCustomerQuery().trim();
            return id && query ? { id, query } : undefined;
        },
        async ({ id, query }) => {
            const res = await api.sellbot.searchCustomers(id, { query });
            if (isOk(res.data)) return res.data.value.names;
            toast?.error(firstError(res.data) ?? t('SELLBOT_STATS_CUSTOMER_SEARCH_FAILED'));
            return [] as string[];
        }
    );

    const pickCustomer = (name: string) => { patchFilters({ player: name }); setCustomerQuery(''); };
    const clearCustomer = () => { patchFilters({ player: null }); setCustomerQuery(''); };

    const rangeFor = (p: RangePreset, from: string, to: string): { from?: string; to?: string } => {
        const now = new Date();
        switch (p) {
            case 'today': return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString() };
            case '7d': return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() };
            case '30d': return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() };
            case '90d': return { from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString() };
            case 'lastMonth': return {
                from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
                to: new Date(new Date(now.getFullYear(), now.getMonth(), 1).getTime() - 1).toISOString(),
            };
            case 'all': return { from: new Date(2000, 0, 1).toISOString() };
            case 'custom': return {
                from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
                to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
            };
        }
    };

    const statsQuery = (filter: StatsFilters, listIds: string[]): SellbotGetStatsParams => {
        const range = rangeFor(filter.preset, filter.customFrom, filter.customTo);
        return {
            from: range.from,
            to: range.to,
            player: filter.player ?? undefined,
            playerListIds: listIds.length > 0 ? listIds.join(',') : undefined,
        };
    };

    const [stats] = createResource(
        () => ({ id: botId(), filters: filters(), listIds: activeListIds() }),
        async (params) => {
            if (!params.id) return null;
            const res = await api.sellbot.getStats(params.id, statsQuery(params.filters, params.listIds));
            return isOk(res.data) ? res.data.value : null;
        }
    );

    const exportStats = async () => {
        const id = botId();
        if (!id) return;
        setExporting(true);
        const failed = await downloadFromApi(getSellbotExportStatsUrl(id, statsQuery(filters(), activeListIds())), `sellbot-stats-${id}.xlsx`);
        setExporting(false);
        if (failed) toast?.error(t('SELLBOT_STATS_EXPORT_FAILED'));
    };

    const refundRate = () => {
        const s = stats();
        if (!s) return 0;
        const total = s.salesCount + s.refundsCount;
        return total > 0 ? (s.refundsCount / total) * 100 : 0;
    };


    const RANGE_PILLS = [
        { value: 'today' as RangePreset, labelKey: 'DATE_RANGE_TODAY' },
        { value: '7d' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_7D' },
        { value: '30d' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_30D' },
        { value: '90d' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_90D' },
        { value: 'lastMonth' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_LAST_MONTH' },
        { value: 'all' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_ALL' },
        { value: 'custom' as RangePreset, labelKey: 'SELLBOT_STATS_RANGE_CUSTOM' },
    ];

    return (
        <div class={styles.container}>
            <SellbotNav />
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="SELLBOT_STATS_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="SELLBOT_STATS_SUBTITLE" /></span>
                </div>
                <button type="button" class="btn btn-sm btn-secondary" disabled={exporting() || !stats()}
                    title={t('SELLBOT_STATS_EXPORT_HELP')} onClick={() => void exportStats()}>
                    <Show when={exporting()} fallback={<><i class="fa-solid fa-file-arrow-down" /> <Text val="SELLBOT_STATS_EXPORT" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" />
                    </Show>
                </button>
            </div>

            <div class="h hcenter" style={{ gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <DateRangePills value={filters().preset} onChange={(preset) => patchFilters({ preset })} pills={RANGE_PILLS} />
                <Show when={filters().preset === 'custom'}>
                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                        <input type="date" value={filters().customFrom} onInput={(e) => patchFilters({ customFrom: e.currentTarget.value })} />
                        <span class={styles.helper}>-</span>
                        <input type="date" value={filters().customTo} onInput={(e) => patchFilters({ customTo: e.currentTarget.value })} />
                    </div>
                </Show>
            </div>

            <div class="h" style={{ gap: 'var(--spacing-md)', 'flex-wrap': 'wrap', 'align-items': 'flex-start' }}>
                <Show when={!filters().player} fallback={
                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                        <span class={`${styles.badge} ${styles.badgeInfo}`}><i class="fa-solid fa-user" /> {filters().player}</span>
                        <button type="button" class="btn btn-sm btn-secondary" onClick={clearCustomer}><i class="fa-solid fa-xmark" /> <Text val="SELLBOT_STATS_CUSTOMER_CLEAR" /></button>
                    </div>
                }>
                    <div class={styles.picker} style={{ 'min-width': '14rem' }}>
                        <input type="text" placeholder={t('SELLBOT_STATS_CUSTOMER_SEARCH_PLACEHOLDER')} value={customerQuery()} onInput={(e) => setCustomerQuery(e.currentTarget.value)} />
                        <Show when={customerQuery()}>
                            <div class={styles.pickerList}>
                                <Show when={!customerSuggestions.loading && debouncedCustomerQuery() === customerQuery()} fallback={<div class={styles.pickerEmpty}><i class="fa-solid fa-spinner fa-spin" /></div>}>
                                    <Show when={(customerSuggestions() ?? []).length > 0} fallback={<div class={styles.pickerEmpty}><Text val="SELLBOT_PICKER_NO_MATCH" /></div>}>
                                        <For each={customerSuggestions()}>{(name) => (
                                            <button type="button" class={styles.pickerItem} onClick={() => pickCustomer(name)}>
                                                <span>{name}</span>
                                            </button>
                                        )}</For>
                                    </Show>
                                </Show>
                            </div>
                        </Show>
                    </div>
                </Show>
                <Show when={playerListEnabled()}>
                    <div style={{ flex: 1, 'min-width': '16rem' }}>
                        <PlayerListAttachPicker available={playerLists() ?? []} attached={selectedLists()}
                            onAttach={attachList} onDetach={detachList}
                            placeholderKey="SELLBOT_STATS_LIST_FILTER_PLACEHOLDER" emptyKey="SELLBOT_STATS_LIST_FILTER_EMPTY" />
                    </div>
                </Show>
            </div>

            <Show when={stats.state !== 'pending'} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3, 4, 5, 6]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                <Show when={stats()} fallback={
                    <div class={styles.empty}><i class="fa-solid fa-chart-line" style={{ 'font-size': '3rem', opacity: 0.5 }} /><p><Text val="SELLBOT_NOT_CONFIGURED" /></p></div>
                }>
                    {(s) => (
                        <>
                            <div class={styles.statsGrid}>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_REVENUE" /></span><span class={styles.statValue}>{fmtMoney(s().totalRevenue)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_SALES" /></span><span class={styles.statValue}>{fmtNum(s().salesCount)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_AVG_SALE" /></span><span class={styles.statValue}>{fmtMoney(s().avgSaleValue)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_REFUND_RATE" /></span><span class={styles.statValue}>{refundRate().toFixed(1)}%</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_ANKAUF_SPEND" /></span><span class={styles.statValue}>{fmtMoney(s().totalAnkaufSpend)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_ANKAUF_COUNT" /></span><span class={styles.statValue}>{fmtNum(s().ankaufCount)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_AVG_ANKAUF" /></span><span class={styles.statValue}>{fmtMoney(s().avgAnkaufValue)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_NET_MARGIN" /></span><span class={styles.statValue}>{fmtMoney(s().netMargin)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_CHAMBER_VALUE" /></span><span class={styles.statValue}>{fmtMoney(s().chamberValueAtAnkaufRate)}</span></div>
                                <div class="fcard"><span class={styles.statLabel}><Text val="SELLBOT_STATS_CHAMBER_VALUE_VERKAUF" /></span><span class={styles.statValue}>{fmtMoney(s().chamberValueAtVerkaufRate)}</span></div>
                            </div>

                            <div class={styles.columns}>
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_STATS_TOP_SELLING" /></h2>
                                    <Show when={s().topSellingItems.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-tags" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_STATS_NO_DATA" /></p></div>}>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <For each={s().topSellingItems}>{(i) => (
                                                <div class={styles.offerRow}>
                                                    <span class={styles.offerName}>{i.bundleName ?? statsItemName(i.itemKey, i.snbt)}</span>
                                                    <span class={styles.offerTiers}>×{formatQuantity(i.count, i.bundleName ? undefined : statsStackSize(i.itemKey))}</span>
                                                    <span class={styles.txnAmount}>{fmtMoney(i.amount)}</span>
                                                </div>
                                            )}</For>
                                        </div>
                                    </Show>
                                </div>

                                <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_STATS_TOP_BOUGHT" /></h2>
                                    <Show when={s().topBoughtItems.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-cart-shopping" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_STATS_NO_DATA" /></p></div>}>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <For each={s().topBoughtItems}>{(i) => (
                                                <div class={styles.offerRow}>
                                                    <span class={styles.offerName}>{statsItemName(i.itemKey, i.snbt)}</span>
                                                    <span class={styles.offerTiers}>×{formatQuantity(i.count, statsStackSize(i.itemKey))}</span>
                                                    <span class={styles.txnAmount}>{fmtMoney(i.amount)}</span>
                                                </div>
                                            )}</For>
                                        </div>
                                    </Show>
                                </div>
                            </div>

                            <div class={styles.columns}>
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_STATS_TOP_BUYERS" /></h2>
                                    <Show when={s().topBuyers.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-users" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_STATS_NO_DATA" /></p></div>}>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <For each={s().topBuyers}>{(p) => (
                                                <div class={styles.offerRow}>
                                                    <span class={styles.offerName}>{p.playerName}</span>
                                                    <span class={styles.offerTiers}>{fmtNum(p.count)}×</span>
                                                    <span class={styles.txnAmount}>{fmtMoney(p.amount)}</span>
                                                </div>
                                            )}</For>
                                        </div>
                                    </Show>
                                </div>

                                <div class="fcard v" style={{ gap: 'var(--spacing-md)', flex: 1 }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_STATS_TOP_SELLERS" /></h2>
                                    <Show when={s().topSellers.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-users" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_STATS_NO_DATA" /></p></div>}>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <For each={s().topSellers}>{(p) => (
                                                <div class={styles.offerRow}>
                                                    <span class={styles.offerName}>{p.playerName}</span>
                                                    <span class={styles.offerTiers}>{fmtNum(p.count)}×</span>
                                                    <span class={styles.txnAmount}>{fmtMoney(p.amount)}</span>
                                                </div>
                                            )}</For>
                                        </div>
                                    </Show>
                                </div>
                            </div>

                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <div class="h hcenter" style={{ 'justify-content': 'space-between', 'flex-wrap': 'wrap' }}>
                                    <h2 class={styles.sectionTitle}><Text val="SELLBOT_STATS_BY_DAY" /></h2>
                                    <select value={dayMetric()} onChange={(e) => setDayMetric(e.currentTarget.value as DayMetric)}>
                                        <For each={DAY_METRICS}>{(m) => <option value={m.value}>{t(m.labelKey)}</option>}</For>
                                    </select>
                                </div>
                                {(() => {
                                    const series = dayMetricSeries(dayMetric(), s(), fmtMoney, fmtNum);
                                    const days = mondayFirst(series.days);
                                    const maxVal = Math.max(1, ...days.map(series.value));
                                    return (
                                        <div class={styles.dayChart}>
                                            <For each={days}>{(d) => (
                                                <div class={styles.dayBarCol}>
                                                    <span class={styles.dayBarValue}>{series.format(series.value(d))}</span>
                                                    <div class={styles.dayBarTrack}>
                                                        <div class={styles.dayBar} style={{ height: `${Math.max(2, (series.value(d) / maxVal) * 100)}%` }} />
                                                    </div>
                                                    <span class={styles.dayBarLabel}>{dayLabel(d.day)}</span>
                                                </div>
                                            )}</For>
                                        </div>
                                    );
                                })()}
                            </div>

                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <div class={styles.sectionHeader}>
                                    <h2><Text val="SELLBOT_STATS_TURNOVER" /></h2>
                                    <span class={styles.helper}><Text val="SELLBOT_STATS_TURNOVER_HELP" /></span>
                                </div>
                                <Show when={s().itemTurnover.length > 0} fallback={<div class={styles.emptySmall}><i class="fa-solid fa-boxes-stacked" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_STATS_NO_DATA" /></p></div>}>
                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <For each={s().itemTurnover}>{(i) => (
                                            <div class={styles.offerRow}>
                                                <span class={styles.offerName}>{statsItemName(i.itemKey, i.snbt)}</span>
                                                <span class={styles.offerTiers}><Text val="SELLBOT_STATS_SOLD" />: {formatQuantity(i.soldCount, statsStackSize(i.itemKey))}</span>
                                                <span class={styles.offerTiers}><Text val="SELLBOT_STOCK" />: {formatQuantity(i.currentStock, statsStackSize(i.itemKey))}</span>
                                                <Show when={i.outOfStockHits > 0}>
                                                    <span class={`${styles.badge} ${styles.badgeDanger}`}>{i.outOfStockHits} × <Text val="SELLBOT_OUT_OF_STOCK" /></span>
                                                </Show>
                                            </div>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>
                        </>
                    )}
                </Show>
            </Show>
        </div>
    );
}
